/**
 * GraphFilters — chip-row filter bar above the RDF graph canvas.
 *
 * Sections: search · node-type chips · predicate chips
 *   (+ overflow popup) · SHACL-only toggle · neighbourhood radius.
 *
 * Visual parity with [EntityFilterChips] — same .glass wrapper,
 * .glass-pill text-xs chip, .input-glass h-8 search box, .kicker
 * w-16 row label. AND across dimensions; OR within each row.
 *
 * Pure presentational + state — the actual filter-set computation
 * lives in [useGraphFilters], so this component just renders and
 * forwards state changes.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type {GraphCatalogResponse, GraphEdge, GraphNode} from "@/api/rdf";
import { ColumnFilterPopup } from "@/components/extraction/ColumnFilterPopup";
import { useDebounce } from "@/hooks/useDebounce";

import {
  emptyFilterState,
  type GraphFilterState,
} from "./useGraphFilters";
import {Glass, GlassPill} from "@/components/glass";

// Fixed node-type chips. Matches the LEGEND constant on StageRdf so
// the legend swatches and the type chips can't drift.
const NODE_TYPES = [
  "Manuscript", "Person", "Work", "Place",
  "Event", "Organization", "Codicological", "Other",
] as const;

const PREDICATE_CHIP_LIMIT = 8;

export interface GraphFiltersProps {
  nodes:        GraphNode[];
  edges:        GraphEdge[];
  catalog:      GraphCatalogResponse | null;
  shaclFocus:   Set<string>;
  selectedId:   string | null;
  state:        GraphFilterState;
  onChange:     (state: GraphFilterState) => void;
  onSearchChange?: (query: string) => void;
  visibleCount: number;
  totalCount:   number;
  corpusTotal:  number;
}

interface ChipProps {
  label:    string;
  active:   boolean;
  count?:   number;
  onToggle: () => void;
  testId?:  string;
}

function Chip({ label, active, count, onToggle, testId }: ChipProps) {
  return (
    <GlassPill as="button" type="button" onClick={onToggle}
      data-testid={testId}
      data-active={active} className={`text-xs ${active ? "border-biu-sky text-biu-sky" : "muted"}`} aria-pressed={active}>
      {label}
      {count !== undefined && <span className="ml-1 opacity-70">({count})</span>}
    </GlassPill>
  );
}

export function GraphFilters({
  nodes,
  edges,
  catalog,
  shaclFocus,
  selectedId,
  state,
  onChange,
  onSearchChange,
  visibleCount,
  totalCount,
  corpusTotal,
}: GraphFiltersProps) {
  // Local draft keeps the input visually responsive on every keystroke.
  // The debounced value feeds the upstream filter call so the O(N+E)
  // pass only runs after the user pauses.
  const [searchDraft, setSearchDraft] = useState(state.query);
  const debouncedSearch = useDebounce(searchDraft, 150);
  const stateRef = useRef(state);
  stateRef.current = state;
  const prevParentQueryRef = useRef(state.query);

  useEffect(() => {
    if (debouncedSearch === stateRef.current.query) return;
    if (onSearchChange) {
      onSearchChange(debouncedSearch);
      return;
    }
    onChange({ ...stateRef.current, query: debouncedSearch });
  }, [debouncedSearch, onChange, onSearchChange]);

  // Reset the draft only when the parent cleared search (Clear all),
  // not on every render while the user is still typing.
  useEffect(() => {
    if (state.query === "" && prevParentQueryRef.current !== "") {
      setSearchDraft("");
    }
    prevParentQueryRef.current = state.query;
  }, [state.query]);

  // Per-type counts for the chip badges.
  const typeCounts = useMemo(() => {
    if (catalog) return catalog.node_types;
    const c: Record<string, number> = {};
    for (const n of nodes) c[n.type] = (c[n.type] ?? 0) + 1;
    return c;
  }, [catalog, nodes]);

  const predicateInfo = useMemo(() => {
    const c: Record<string, number> = catalog
      ? {...catalog.edge_predicates}
      : {};
    if (!catalog) {
      for (const e of edges) {
        const lbl = e.predicate_label || e.predicate || "";
        if (!lbl) continue;
        c[lbl] = (c[lbl] ?? 0) + 1;
      }
    }
    const ordered = Object.entries(c)
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
    return {
      all: ordered,
      head: ordered.slice(0, PREDICATE_CHIP_LIMIT),
      tail: ordered.slice(PREDICATE_CHIP_LIMIT),
    };
  }, [catalog, edges]);

  const [predOverflow, setPredOverflow] = useState<{ x: number; y: number } | null>(null);

  function toggleType(t: string) {
    const next = new Set(state.types);
    if (next.has(t)) next.delete(t); else next.add(t);
    onChange({ ...state, types: next });
  }
  function togglePredicate(p: string) {
    const next = new Set(state.predicates);
    if (next.has(p)) next.delete(p); else next.add(p);
    onChange({ ...state, predicates: next });
  }
  function toggleShacl() {
    onChange({ ...state, shaclOnly: !state.shaclOnly });
  }
  function setRadius(r: 0 | 1 | 2) {
    onChange({ ...state, radius: state.radius === r ? 0 : r });
  }
  function clearAll() {
    setSearchDraft("");
    onChange(emptyFilterState());
  }

  const anyActive =
    state.query.length > 0 ||
    state.types.size > 0 ||
    state.predicates.size > 0 ||
    state.shaclOnly ||
    state.radius > 0;

  return (
    <Glass variant="compact" className="space-y-3 p-3" data-testid="graph-filters">
      {/* Search + visible-count + clear */}
      <div className="flex items-center gap-2">
        <input type="text"
          placeholder="Search nodes, properties, edge predicates…"
          data-testid="graph-search"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          className="input-glass h-8 flex-1 text-sm" />
        <span className="muted text-xs whitespace-nowrap"
              data-testid="graph-visible-count">
          {visibleCount} / {totalCount} in view
          {corpusTotal > totalCount && (
            <span className="opacity-70"> · {corpusTotal.toLocaleString()} corpus</span>
          )}
        </span>
        {anyActive && (
          <button type="button" onClick={clearAll}
            data-testid="graph-clear-filters"
            className="button-ghost h-8 px-3 text-xs">
            Clear all
          </button>
        )}
      </div>

      {/* Type chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="graph-filter-row-type">
        <span className="kicker w-20">Node type</span>
        {NODE_TYPES.map((t) => (
          <Chip key={t} label={t}
                testId={`graph-chip-type-${t}`}
                active={state.types.has(t)}
                count={
                  t === "Manuscript" && catalog
                    ? catalog.manuscript_count
                    : (typeCounts[t] ?? 0)
                }
                onToggle={() => toggleType(t)} />
        ))}
      </div>

      {/* Predicate chips */}
      {predicateInfo.all.length > 0 && (
        <div className="flex flex-wrap items-center gap-2"
             data-testid="graph-filter-row-predicate">
          <span className="kicker w-20">Edge type</span>
          {predicateInfo.head.map((p) => (
            <Chip key={p.label} label={p.label}
                  testId={`graph-chip-pred-${slug(p.label)}`}
                  active={state.predicates.has(p.label)}
                  count={p.count}
                  onToggle={() => togglePredicate(p.label)} />
          ))}
          {predicateInfo.tail.length > 0 && (
            <GlassPill
              as="button"
              type="button"
              data-testid="graph-pred-overflow"
              className="text-xs muted"
              onClick={(ev) => setPredOverflow({
                x: ev.clientX, y: ev.clientY + 4,
              })}
            >
              +{predicateInfo.tail.length} more…
            </GlassPill>
          )}
        </div>
      )}

      {/* SHACL + neighbourhood */}
      {(shaclFocus.size > 0 || selectedId) && (
        <div className="flex flex-wrap items-center gap-2"
             data-testid="graph-filter-row-extra">
          <span className="kicker w-20">Scope</span>
          {shaclFocus.size > 0 && (
            <Chip label={`SHACL flagged (${shaclFocus.size})`}
                  testId="graph-chip-shacl"
                  active={state.shaclOnly}
                  onToggle={toggleShacl} />
          )}
          {selectedId && (
            <>
              <Chip label="1 hop"
                    testId="graph-chip-radius-1"
                    active={state.radius === 1}
                    onToggle={() => setRadius(1)} />
              <Chip label="2 hops"
                    testId="graph-chip-radius-2"
                    active={state.radius === 2}
                    onToggle={() => setRadius(2)} />
            </>
          )}
        </div>
      )}

      {predOverflow && (
        <ColumnFilterPopup
          columnLabel="Edge type"
          values={predicateInfo.tail.map(p => p.label)}
          selected={new Set(
            [...state.predicates].filter(p =>
              predicateInfo.tail.some(t => t.label === p),
            ),
          )}
          x={predOverflow.x}
          y={predOverflow.y}
          onApply={(picked) => {
            // Merge picked tail values with currently-active head
            // predicates (so the popup doesn't drop the chip
            // selections that were already on).
            const headActive = new Set(
              [...state.predicates].filter(p =>
                predicateInfo.head.some(h => h.label === p),
              ),
            );
            onChange({
              ...state,
              predicates: new Set([...headActive, ...picked]),
            });
            setPredOverflow(null);
          }}
          onCancel={() => setPredOverflow(null)}
        />
      )}
    </Glass>
  );
}

function slug(s: string): string {
  return s.toLocaleLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
